import { OVERLAY_PRIO, useOverlaySlot } from './lib/overlays';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import Home from './pages/Home';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import SplashScreen from './components/SplashScreen';
import Loader from './components/Loader';
import * as api from './lib/api';
import type { User } from './lib/types';
import LoginScreen, { TelegramWidgetUser, loadSavedBrowserAuth } from './components/LoginScreen';
import AgreementText from './components/AgreementText';

// Lazy-loaded pages
const Rating = lazy(() => import('./pages/Rating'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const Menu = lazy(() => import('./pages/Menu'));
const Orders = lazy(() => import('./pages/Orders'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'));
const LevelsTable = lazy(() => import('./pages/LevelsTable'));
const Admin = lazy(() => import('./pages/Admin'));
const ProfileRoom = lazy(() => import('./pages/ProfileRoom'));
const Badges = lazy(() => import('./pages/Badges'));
const About = lazy(() => import('./pages/About'));
const Support = lazy(() => import('./pages/Support'));
const Streak = lazy(() => import('./pages/Streak'));
const WelcomeStories = lazy(() => import('./components/WelcomeStories'));
const TournamentClock = lazy(() => import('./pages/TournamentClock'));
const TournamentClockLive = lazy(() => import('./pages/TournamentClockLive'));
const Teams = lazy(() => import('./pages/Teams'));

const WELCOME_STORAGE_KEY = 'welcome_seen_v1';
const AGREEMENT_STORAGE_KEY = 'user_agreement_accepted_v2';
// The splash plays on app launch, not on every page load: iOS/Telegram often
// evict the webview in the background and a deploy triggers one auto-reload.
const SPLASH_STORAGE_KEY = 'splash_last_shown';
const SPLASH_MIN_GAP_MS = 30 * 60 * 1000;

function shouldShowSplash(): boolean {
  try {
    const last = Number(localStorage.getItem(SPLASH_STORAGE_KEY) || 0);
    if (Date.now() - last < SPLASH_MIN_GAP_MS) return false;
    localStorage.setItem(SPLASH_STORAGE_KEY, String(Date.now()));
  } catch { /* storage blocked — just show it */ }
  return true;
}
// Decided once per page load (outside React so StrictMode can't evaluate it twice).
const SHOW_SPLASH_ON_BOOT = shouldShowSplash();

function UserAgreementModal({ onAccept }: { onAccept: () => void }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#060606',
      display: 'flex', flexDirection: 'column',
      padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
    }}>
      <h1 style={{
        textAlign: 'center', fontSize: 18, fontWeight: 800,
        color: '#f2c14b', marginBottom: 4,
        fontFamily: "'Unbounded', sans-serif",
      }}>
        ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ
      </h1>
      <p style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
        Правила посещения и участия в мероприятиях HEADS UP
      </p>
      <div
        onScroll={handleScroll}
        style={{
          flex: 1, overflowY: 'auto', padding: '20px',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)',
          fontSize: 13, lineHeight: 1.7,
          color: 'rgba(255,255,255,0.75)',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <AgreementText />
      </div>
      <button
        onClick={onAccept}
        disabled={!scrolledToBottom}
        style={{
          marginTop: 16, width: '100%', padding: '16px',
          borderRadius: 14, border: 'none', cursor: scrolledToBottom ? 'pointer' : 'not-allowed',
          background: scrolledToBottom ? 'linear-gradient(135deg, #f2c14b 0%, #d79a2b 100%)' : 'rgba(255,255,255,0.08)',
          color: scrolledToBottom ? '#000' : 'rgba(255,255,255,0.3)',
          fontSize: 16, fontWeight: 700,
          fontFamily: "'Unbounded', sans-serif",
          transition: 'all 0.3s',
        }}
      >
        {scrolledToBottom ? 'Согласен' : 'Прочитайте до конца ↓'}
      </button>
    </div>
  );
}

function NoShowWarningModal({ onClose }: { onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99998,
      background: 'rgba(0,0,0,0.75)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        maxWidth: 360, width: '100%', background: '#141414',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 24,
        padding: '32px 24px', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16,
          background: 'rgba(255,140,0,0.1)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', fontSize: 32,
        }}>⚡</div>
        <h2 style={{
          color: '#fff', fontSize: 18, fontWeight: 800, margin: '0 0 8px',
          fontFamily: "'Unbounded', sans-serif",
        }}>
          Вы пропустили турнир
        </h2>
        <p style={{
          color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.6,
          margin: '0 0 16px',
        }}>
          Вы были записаны на турнир, но не пришли и не отменили запись.
        </p>
        <div style={{
          padding: '14px 16px', borderRadius: 12,
          background: 'rgba(255,140,0,0.06)', border: '1px solid rgba(255,140,0,0.12)',
          fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.6)',
          textAlign: 'left', marginBottom: 20,
        }}>
          <span style={{ color: '#ff8c00', fontWeight: 700 }}>Пожалуйста, отменяйте запись заранее</span>, чтобы не занимать место у других игроков. При повторных пропусках регистрация на турниры может быть ограничена.
        </div>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: 14, borderRadius: 14, border: 'none',
            background: 'linear-gradient(135deg, #f2c14b 0%, #d79a2b 100%)',
            color: '#000', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            fontFamily: "'Unbounded', sans-serif",
          }}
        >
          Понятно
        </button>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSplash, setShowSplash] = useState(SHOW_SPLASH_ON_BOOT);
  const [noShowWarning, setNoShowWarning] = useState(false);
  const [initTimedOut, setInitTimedOut] = useState(false);
  const [initLogs, setInitLogs] = useState<string[]>([]);

  const isAdminRoute = window.location.pathname.startsWith('/admin');

  const [showAgreement, setShowAgreement] = useState<boolean>(() => {
    try { return localStorage.getItem(AGREEMENT_STORAGE_KEY) !== '1'; } catch { return true; }
  });
  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    try {
      const forceWelcome = new URLSearchParams(window.location.search).get('welcome') === '1';
      if (forceWelcome) return true;
      return localStorage.getItem(WELCOME_STORAGE_KEY) !== '1';
    } catch {
      return false;
    }
  });

  // Startup popups take turns (see lib/overlays.ts). These top ones are
  // already sequenced here; registering them makes everything else wait.
  useOverlaySlot('agreement', showAgreement && !isAdminRoute, OVERLAY_PRIO.agreement, true);
  useOverlaySlot('splash', !showAgreement && showSplash && !isAdminRoute, OVERLAY_PRIO.splash, true);
  useOverlaySlot('welcome', !showAgreement && !!user && showWelcome && !showSplash && !isAdminRoute, OVERLAY_PRIO.welcome, true);
  const noShowVisible = useOverlaySlot('no-show', noShowWarning, OVERLAY_PRIO.noShow, true);

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.disableVerticalSwipes();
      if (typeof (WebApp as any).requestFullscreen === 'function') {
        (WebApp as any).requestFullscreen();
      }

      // Set Telegram safe area CSS variables
      const wa = WebApp as any;
      const applySafeArea = () => {
        const sa = wa.safeAreaInset || {};
        const csa = wa.contentSafeAreaInset || {};
        const root = document.documentElement;
        root.style.setProperty('--tg-safe-area-inset-top', `${sa.top || 0}px`);
        root.style.setProperty('--tg-safe-area-inset-bottom', `${sa.bottom || 0}px`);
        root.style.setProperty('--tg-content-safe-area-inset-top', `${csa.top || 0}px`);
        root.style.setProperty('--tg-content-safe-area-inset-bottom', `${csa.bottom || 0}px`);
      };
      applySafeArea();
      wa.onEvent?.('safeAreaChanged', applySafeArea);
      wa.onEvent?.('contentSafeAreaChanged', applySafeArea);
    } catch (e) {
      console.log('WebApp недоступен (разработка)');
    }

    // Получаем данные пользователя из Telegram
    const log = (msg: string) => setInitLogs(prev => [...prev, `${new Date().toISOString().slice(11,19)} ${msg}`]);

    const initUser = async () => {
      try {
        log(`start ua=${navigator.userAgent.slice(0,60)}`);
        // Capture referral param before any auth flow
        const _refParams = new URLSearchParams(window.location.search);
        const _refCode = _refParams.get('ref');
        if (_refCode) {
          try { localStorage.setItem('referral_code', _refCode); } catch {}
          const url = new URL(window.location.href);
          url.searchParams.delete('ref');
          window.history.replaceState({}, '', url.pathname + url.search);
        }
        // Also check Telegram start_param for referral (ref_XX format)
        const _tgStartParam = WebApp?.initDataUnsafe?.start_param;
        if (_tgStartParam && _tgStartParam.startsWith('ref_')) {
          try { localStorage.setItem('referral_code', _tgStartParam.replace('ref_', '')); } catch {}
        }

        // Demo access via secret link (temporary — remove when no longer needed)
        // URL: https://heads-up-poker.ru/?demo=SB2025headsup
        const _demoParams = new URLSearchParams(window.location.search);
        const _demoKey = _demoParams.get('demo');
        if (_demoKey === 'SB2025headsup') {
          window.history.replaceState({}, '', window.location.pathname);
          const demoUser = await api.upsertUser({
            telegram_id: 888888888,
            username: 'demo_viewer',
            first_name: 'Демо',
            last_name: 'Просмотр',
          });
          setUser({ ...demoUser, tg_photo_url: null });
          setLoading(false);
          return;
        }
        // END demo access

        const tgUser = WebApp?.initDataUnsafe?.user;
        log(tgUser ? `tg_user=${tgUser.id}` : 'no_tg_user');

        if (tgUser) {

          let userData: User | null = null;
          let lastError: unknown = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              log(`upsertUser attempt=${attempt + 1}`);
              userData = await Promise.race([
                api.upsertUser({
                  telegram_id: tgUser.id,
                  username: tgUser.username,
                  first_name: tgUser.first_name,
                  last_name: tgUser.last_name,
                }),
                new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 20000)),
              ]);
              log('upsertUser ok');
              break;
            } catch (e) {
              const msg = e instanceof Error ? e.message : String(e);
              log(`upsertUser err: ${msg}`);
              lastError = e;
              if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            }
          }
          if (!userData) throw lastError;
          setUser({ ...userData, tg_photo_url: tgUser.photo_url });
        } else {
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          if (isLocalhost) {
            // Fallback только для локальной разработки
            console.warn('WebApp.initDataUnsafe?.user не доступен — локальная разработка');
            try {
              const devUser = await api.upsertUser({
                telegram_id: 999999999,
                username: 'dev_preview',
                first_name: 'Разработка',
                last_name: '',
              });
              setUser({ ...devUser, tg_photo_url: null });
            } catch {
              console.warn('Supabase недоступен, используем мок');
              setUser({
                id: 0, telegram_id: 999999999, username: 'dev_preview',
                first_name: 'Разработка', last_name: '', display_name: 'Разработка',
                tg_photo_url: null,
              });
            }
          } else {
            // В браузере — пробуем загрузить сохранённую авторизацию
            // 1. Supabase Auth session (email login)
            log('checking_session');
            const session = await Promise.race([
              api.getAuthSession(),
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 8000)),
            ]).catch(() => null);
            if (session?.user?.email) {
              log(`session_found email=${session.user.email}`);
              try {
                const userData = await Promise.race([
                  api.upsertUserByEmail(session.user.email, session.user.id),
                  new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 12000)),
                ]);
                setUser({ ...userData, tg_photo_url: null });
                setLoading(false);
                return;
              } catch (e) {
                log(`upsertByEmail err: ${e instanceof Error ? e.message : String(e)}`);
                // Fall through to other methods
              }
            } else {
              log('no_session');
            }

            // 2. Telegram widget saved auth
            const savedAuth = loadSavedBrowserAuth();
            if (savedAuth) {
              log(`saved_tg_auth id=${savedAuth.id}`);
              try {
                const userData = await Promise.race([
                  api.upsertUser({
                    telegram_id: savedAuth.id,
                    username: savedAuth.username,
                    first_name: savedAuth.first_name,
                    last_name: savedAuth.last_name,
                  }),
                  new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 12000)),
                ]);
                setUser({ ...userData, tg_photo_url: savedAuth.photo_url ?? null });
              } catch (e) {
                log(`savedAuth err: ${e instanceof Error ? e.message : String(e)}`);
                setError('no_telegram');
              }
            } else {
              log('no_saved_auth → show_login');
              setError('no_telegram');
            }
          }
        }
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        log(`fatal: ${msg}`);
        console.error('Ошибка при инициализации пользователя:', error);
        setError(`Ошибка инициализации: ${msg}`);
      } finally {
        setLoading(false);
      }
    };

    initUser();

    // If still loading after 15s — show retry button
    const timeout = setTimeout(() => {
      setInitTimedOut(true);
    }, 15000);
    return () => clearTimeout(timeout);
  }, []);

  const handleWelcomeDone = () => {
    try {
      localStorage.setItem(WELCOME_STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setShowWelcome(false);
  };

  // Check push notification status (don't auto-request — needs user gesture)
  const [pushStatus, setPushStatus] = useState<'unknown' | 'subscribed' | 'available' | 'unsupported'>('unknown');
  useEffect(() => {
    if (!user?.id) return;
    import('./lib/pushNotifications').then(({ isPushSubscribed, isPushSupported }) => {
      isPushSupported().then(supported => {
        if (!supported) { setPushStatus('unsupported'); return; }
        isPushSubscribed().then(sub => setPushStatus(sub ? 'subscribed' : 'available'));
      });
    });
  }, [user?.id]);

  const handleEnablePush = async () => {
    if (!user?.id) return;
    const { registerPushNotifications } = await import('./lib/pushNotifications');
    const ok = await registerPushNotifications(user.id);
    setPushStatus(ok ? 'subscribed' : 'available');
  };

  // Check for no-shows when user loads
  useEffect(() => {
    if (!user?.id) return;
    api.checkNoShows(user.id).then(result => {
      if (result.hasNoShow) setNoShowWarning(true);
    }).catch(() => {});
  }, [user?.id]);

  // Email auth callback
  const handleEmailAuth = useCallback((userData: User) => {
    setUser(userData);
    setError(null);
  }, []);

  // Telegram widget auth callback
  const handleBrowserTgAuth = useCallback(async (tgUser: TelegramWidgetUser) => {
    try {
      const userData = await api.upsertUser({
        telegram_id: tgUser.id,
        username: tgUser.username,
        first_name: tgUser.first_name,
        last_name: tgUser.last_name,
      });
      setUser({ ...userData, tg_photo_url: tgUser.photo_url ?? null });
      setError(null);
    } catch (e: unknown) {
      setError(`Ошибка входа: ${e instanceof Error ? e.message : 'Неизвестная ошибка'}`);
    }
  }, []);

  // Admin works without main auth — handles its own login/password flow
  if (window.location.pathname.startsWith('/admin')) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/admin" element={<Admin user={null as any} />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  // Tournament clock works without auth
  if (window.location.pathname === '/clock' || window.location.pathname === '/clock/live') {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/clock" element={<TournamentClock />} />
              <Route path="/clock/live" element={<TournamentClockLive />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  if (loading) {
    if (initTimedOut) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          alignItems: 'center', height: '100vh', padding: '32px',
          textAlign: 'center', background: '#060606', color: '#fff', gap: 16,
        }}>
          <img src="/stories-bg-1.svg" alt="HU" style={{ width: 64, height: 64, opacity: 0.5 }} />
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0 }}>
            Проблема с подключением
          </p>
          {initLogs.length > 0 && (
            <div style={{
              width: '100%', maxWidth: 380, background: 'rgba(255,255,255,0.04)',
              borderRadius: 10, padding: '12px 14px', textAlign: 'left',
            }}>
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginBottom: 6 }}>
                Лог (скопируй и отправь разработчику):
              </div>
              <pre style={{
                margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.6)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6,
              }}>
                {initLogs.join('\n')}
              </pre>
            </div>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '14px 32px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #f2c14b 0%, #d79a2b 100%)',
              color: '#000', fontSize: 15, fontWeight: 700,
              fontFamily: "'Unbounded', sans-serif",
            }}
          >
            Повторить
          </button>
        </div>
      );
    }
    return <Loader />;
  }

  if (!user && !loading) {
    if (error === 'no_telegram') {
      return <LoginScreen onAuth={handleEmailAuth} onTelegramAuth={handleBrowserTgAuth} />;
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        color: 'var(--tg-theme-text-color, #ffffff)',
        background: 'var(--tg-theme-bg-color, #0a0a0a)'
      }}>
        <h2 style={{ marginBottom: '16px', color: '#ff4444' }}>⚠️ Ошибка инициализации</h2>
        <p style={{ marginBottom: '8px', color: '#888' }}>
          {error || 'Не удалось инициализировать пользователя.'}
        </p>
        <div style={{ marginTop: '24px', fontSize: '12px', color: '#666' }}>
          <p style={{ marginTop: '8px' }}>
            Откройте консоль браузера (F12) для подробностей.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/levels" element={<LevelsTable user={user!} />} />
        <Route path="/clock" element={<TournamentClock />} />
        <Route path="/" element={<Layout userId={user?.id}><Home user={user!} /></Layout>} />
        <Route path="/rating" element={<Layout userId={user?.id}><Rating user={user!} /></Layout>} />
        <Route path="/teams" element={<Layout userId={user?.id}><Teams user={user!} /></Layout>} />
        <Route path="/tournaments" element={<Layout userId={user?.id}><Tournaments user={user!} /></Layout>} />
        <Route path="/menu" element={<Layout userId={user?.id}><Menu user={user!} /></Layout>} />
        <Route path="/orders" element={<Layout userId={user?.id}><Orders user={user!} /></Layout>} />
        <Route path="/profile" element={<Layout userId={user?.id}><ProfileRoom user={user!} /></Layout>} />
        <Route path="/profile/:userId" element={<Layout userId={user?.id}><ProfileRoom user={user!} /></Layout>} />
        <Route path="/badges" element={<Layout userId={user?.id}><Badges user={user!} /></Layout>} />
        <Route path="/badges/:userId" element={<Layout userId={user?.id}><Badges user={user!} /></Layout>} />
        <Route path="/about" element={<Layout userId={user?.id}><About /></Layout>} />
        <Route path="/support" element={<Layout userId={user?.id}><Support /></Layout>} />
        <Route path="/streak" element={<Layout userId={user?.id}><Streak user={user!} /></Layout>} />
        <Route path="/tournament/:id" element={<Layout userId={user?.id}><TournamentDetail user={user!} /></Layout>} />
        <Route path="/admin" element={<Admin user={user!} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>

      {showAgreement && !isAdminRoute && (
        <UserAgreementModal onAccept={() => {
          try { localStorage.setItem(AGREEMENT_STORAGE_KEY, '1'); } catch {}
          setShowAgreement(false);
        }} />
      )}

      {!showAgreement && showSplash && !isAdminRoute && (
        <SplashScreen onDone={() => setShowSplash(false)} />
      )}

      {!showAgreement && user && showWelcome && !showSplash && !isAdminRoute && (
        <WelcomeStories userFirstName={user?.display_name || user?.username || user?.first_name} onDone={handleWelcomeDone} />
      )}

      {noShowVisible && <NoShowWarningModal onClose={() => setNoShowWarning(false)} />}
    </>
  );
}

export default App;

